// Cartelera TV — contrato de la pantalla del backoffice con el televisor.
//
// El televisor del local está montado en VERTICAL (9:16). Eso decide las dos cosas que
// importan acá: la medida a la que se genera cada imagen y la forma del preview.
//
// A diferencia de la portada del home —cuatro medidas, porque la caja del hero cambia de
// forma con el ancho de la ventana— acá hay UNA sola caja y no se mueve nunca: es una
// pantalla fija, siempre del mismo tamaño, mirada siempre desde la misma distancia. Por eso
// el archivo se genera una vez y lo que se ve en el preview es exactamente lo que se
// proyecta, sin el "segundo recorte" del navegador que hay que simular en la portada.

import { get, post, patch, del, upload } from './apiClient';

/** La medida a la que se genera cada imagen: 1080x1920 es el panel de un televisor Full HD rotado. */
export const ANCHO_SALIDA = 1080;
export const ALTO_SALIDA = 1920;

/**
 * Mínimo aceptado. Se miran las DOS dimensiones: recortar "cover" a 1080x1920 necesita
 * `ancho >= 1080` y `alto >= 1920`. Mirar sólo una deja pasar una panorámica que después sale
 * chica y borrosa en una pantalla de 50 pulgadas, que es donde más se nota.
 *
 * El piso es 720x1280 (un HD vertical): abajo de eso la imagen se genera igual pero en la
 * resolución que soporte el original, y a tres metros se ve el pixelado.
 */
export const ANCHO_MINIMO = 720;
export const ALTO_MINIMO = 1280;

/**
 * Los tipos de bloque. `kind` tiene que coincidir con el ENUM de `tv_block` y con KINDS en
 * TvController: son el mismo contrato escrito de los dos lados.
 *
 * `ayuda` es lo que decide si el operador elige bien, así que dice qué se muestra Y de dónde
 * sale — el valor de casi todos estos bloques es que NO hay que mantenerlos.
 */
export const TIPOS = [
    {
        kind: 'image',
        nombre: 'Imagen',
        icono: 'image',
        ayuda: 'Una foto o placa que subís vos. Es lo único que no se actualiza solo.',
        llevaArchivo: true,
    },
    {
        kind: 'events',
        nombre: 'Próximos eventos',
        icono: 'event',
        ayuda: 'Los eventos publicados que todavía no pasaron, con su póster y su fecha. Se arma solo con lo que ya está cargado en Eventos.',
        llevaItems: true,
        etiquetaItems: 'CUÁNTOS EVENTOS',
        itemsDefault: 4,
    },
    {
        kind: 'news',
        nombre: 'Nuevos ingresos',
        icono: 'new_releases',
        ayuda: 'Los juegos de la colección "Novedades", con foto y precio. Es el mismo carrusel que ve el cliente en la home.',
        llevaItems: true,
        etiquetaItems: 'CUÁNTOS JUEGOS',
        itemsDefault: 6,
    },
    {
        kind: 'offers',
        nombre: 'Ofertas',
        icono: 'local_offer',
        ayuda: 'Los juegos con descuento vigente, con el precio tachado y el final.',
        llevaItems: true,
        etiquetaItems: 'CUÁNTOS JUEGOS',
        itemsDefault: 6,
    },
    {
        kind: 'cafeteria_menu',
        nombre: 'Menú de cafetería',
        icono: 'restaurant_menu',
        ayuda: 'La carta con precios, como pizarra. Sale del menú del POS: lo que sacás de la carta desaparece del televisor solo.',
    },
    {
        kind: 'tournament',
        nombre: 'Tabla de torneo',
        icono: 'emoji_events',
        ayuda: 'Las posiciones en vivo. Si no elegís uno, muestra el torneo que se esté jugando en ese momento y no aparece el resto de la semana.',
        llevaTorneo: true,
        llevaItems: true,
        etiquetaItems: 'CUÁNTAS POSICIONES',
        itemsDefault: 8,
    },
    {
        kind: 'event',
        nombre: 'Un evento',
        icono: 'event_available',
        ayuda: 'Un solo evento a pantalla completa: póster, fecha, entradas con precio, el cupo y un QR para sacar la entrada. Si no elegís uno, muestra el próximo evento publicado. Cuando el evento termina, el bloque deja de aparecer.',
        llevaEvento: true,
    },
];

export const tipoDe = (kind) => TIPOS.find((t) => t.kind === kind) || null;

export const nombreDeTipo = (kind) => (tipoDe(kind) || {}).nombre || kind;

/** "1 min 30 s" para duraciones largas, "12 s" para las cortas. */
export const formatoDuracion = (segundos) => {
    const n = Number(segundos) || 0;
    if (n < 60) return `${n} s`;
    const min = Math.floor(n / 60);
    const resto = n % 60;
    return resto === 0 ? `${min} min` : `${min} min ${resto} s`;
};

/**
 * Cuánto dura una vuelta entera. Es el número que de verdad le importa al operador: con 12
 * bloques de 15 segundos, una promo nueva se ve una vez cada tres minutos, y eso decide si
 * conviene agregar otro bloque o acortar los que están.
 */
export const duracionDeLaVuelta = (bloques) => bloques
    .filter((b) => b.isActive)
    .reduce((total, b) => total + Number(b.durationSeconds || 0), 0);

// ── API ──────────────────────────────────────────────────────────────────────────────────

export const getScreens = () => get('manage-tv/screens');

export const createScreen = (screen) => post('manage-tv/screens', { screen });

export const deleteScreen = (screen) => del(`manage-tv/screens/${encodeURIComponent(screen)}`);

/**
 * El nombre de una pantalla tal como lo va a aceptar el backend, y tal como va a quedar en la
 * URL del televisor.
 *
 * Se normaliza acá, mientras el operador escribe, en vez de rechazarle "Vidriera Principal"
 * con un error: nadie tiene por qué saber que el nombre termina en una URL. Escribe lo que
 * quiere, ve abajo el resultado (`vidriera-principal`) y decide.
 */
export const normalizarPantalla = (texto) => String(texto || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // saca los acentos: "salón" -> "salon"
    .replace(/[^a-z0-9]+/g, '-')                        // todo lo demás es separador
    .replace(/^-+|-+$/g, '')                            // sin guiones al principio ni al final
    .slice(0, 32);

// ── Fondo ────────────────────────────────────────────────────────────────────────────────
//
// Dos niveles. El de la PANTALLA se sube una vez y lo usan todos sus bloques; el de un bloque
// lo pisa sólo para ése. El televisor no ve esta distinción: el backend le manda la URL ya
// resuelta.

export const getScreenSettings = (screen) => get(`manage-tv/screen?screen=${encodeURIComponent(screen)}`);

export const updateScreenSettings = (screen, data) => patch(`manage-tv/screen?screen=${encodeURIComponent(screen)}`, data);

export const uploadScreenBackground = (screen, blob) => upload(`image/tv/screen/${encodeURIComponent(screen)}/background`, blob, 'fondo');

export const clearScreenBackground = (screen) => del(`manage-tv/screen/background?screen=${encodeURIComponent(screen)}`);

export const uploadBlockBackground = (blockId, blob) => upload(`image/tv/${blockId}/background`, blob, `fondo-${blockId}`);

export const clearBlockBackground = (blockId) => del(`manage-tv/blocks/${blockId}/background`);

export const getBlocks = (screen) => get(`manage-tv/blocks?screen=${encodeURIComponent(screen)}`);

// Todos los eventos, no sólo los publicados: para decir POR QUÉ un bloque no se ve ("es un
// borrador", "se canceló") hace falta ver el evento aunque el televisor no lo vaya a mostrar.
export const getEventsForTv = () => get('manage-events');

export const createBlock = (data) => post('manage-tv/blocks', data);

export const updateBlock = (id, data) => patch(`manage-tv/blocks/${id}`, data);

export const moveBlock = (id, direction) => post(`manage-tv/blocks/${id}/move`, { direction });

export const deleteBlock = (id) => del(`manage-tv/blocks/${id}`);

// La imagen va por la ruta de imágenes (POST /image/tv/:blockId), con el resto de las
// escrituras al bucket: es donde vive el multer y la validación de magic bytes. El bloque
// tiene que existir antes, porque el nombre del objeto es su id.
export const uploadBlockImage = (blockId, blob) => upload(`image/tv/${blockId}`, blob, `tv-${blockId}`);
